import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, Plus, Edit2, Trash2, Building2, MoreVertical,
  Navigation, Lock, AlertCircle, Check, Loader2, ExternalLink, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import axios from 'axios';
import UpgradePlanModal from '../components/UpgradePlanModal';
import LocationMapPicker from '../components/LocationMapPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LocationsPage = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeStatus, setGeocodeStatus] = useState(null); // 'success', 'error', null
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [showManualCoords, setShowManualCoords] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    latitude: null,
    longitude: null,
    geofence_radius_m: 100,
    staff_pin: ''
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Error al cargar locales');
    } finally {
      setLoading(false);
    }
  };

  const handleGeocode = async () => {
    if (!formData.address.trim()) {
      toast.error('Introduce una dirección');
      return;
    }

    setGeocoding(true);
    setGeocodeStatus(null);
    try {
      const response = await axios.post(`${API}/locations/geocode?address=${encodeURIComponent(formData.address)}`);
      setFormData(prev => ({
        ...prev,
        latitude: response.data.latitude,
        longitude: response.data.longitude
      }));
      setShowManualCoords(true); // Always show manual coords to allow verification
      setGeocodeStatus('success');
      toast.success('¡Dirección encontrada! Verifica las coordenadas en Google Maps y ajústalas si es necesario.');
    } catch (error) {
      console.error('Error geocoding:', error);
      setGeocodeStatus('error');
      toast.error('No se pudo identificar la dirección. Introduce las coordenadas manualmente.');
      setShowManualCoords(true);
    } finally {
      setGeocoding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate PIN
    if (!formData.staff_pin || formData.staff_pin.length < 4 || formData.staff_pin.length > 6 || !/^\d+$/.test(formData.staff_pin)) {
      toast.error('El PIN debe tener entre 4 y 6 dígitos');
      return;
    }

    // Validate coordinates
    if (formData.latitude === null || formData.longitude === null) {
      toast.error('Por favor, introduce las coordenadas de tu local');
      return;
    }

    // Log what we're sending
    console.log('Sending location data:', formData);

    try {
      if (editingLocation) {
        await axios.put(`${API}/locations/${editingLocation.id}`, formData);
        toast.success('Local actualizado');
      } else {
        await axios.post(`${API}/locations`, formData);
        toast.success('Local creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      // Check if it's a plan limit error
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response?.data?.detail || 'Has alcanzado el límite de locales de tu plan actual.');
        setUpgradeModalOpen(true);
        setDialogOpen(false);
      } else {
        toast.error(error.response?.data?.detail || 'Error al guardar');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este local? No podrás eliminarlo si tiene dispositivos QR asociados.')) return;
    try {
      await axios.delete(`${API}/locations/${id}`);
      toast.success('Local eliminado');
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      latitude: location.latitude,
      longitude: location.longitude,
      geofence_radius_m: location.geofence_radius_m,
      staff_pin: location.staff_pin
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingLocation(null);
    setShowManualCoords(false);
    setGeocodeStatus(null);
    setFormData({
      name: '',
      address: '',
      latitude: null,
      longitude: null,
      geofence_radius_m: 100,
      staff_pin: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="locations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Locales</h1>
          <p className="text-slate-600">Configura tus establecimientos para la validación por geolocalización</p>
        </div>
        <Button 
          className="btn-primary"
          onClick={() => { resetForm(); setDialogOpen(true); }}
          data-testid="create-location-btn"
        >
          <Plus className="w-5 h-5" /> Nuevo local
        </Button>
      </div>

      {/* Info Card */}
      <motion.div 
        className="bg-blue-50 border border-blue-200 rounded-xl p-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-start gap-3">
          <Navigation className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">Validación por geolocalización</h3>
            <p className="text-sm text-blue-700 mt-1">
              Cuando asocias un local a un QR, los clientes deberán estar físicamente cerca del establecimiento para registrar su visita. 
              Si no pueden usar la geolocalización, pueden usar el PIN de respaldo que configures.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Locations List */}
      {locations.length === 0 ? (
        <motion.div 
          className="card-dashboard text-center py-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Sin locales todavía</h3>
          <p className="text-slate-600 mb-6">Añade tu primer local para activar la validación por geolocalización</p>
          <Button 
            className="btn-primary"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="w-5 h-5" /> Añadir local
          </Button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((location, i) => (
            <motion.div
              key={location.id}
              className="card-dashboard group hover:shadow-card-hover transition-all"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`location-card-${location.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{location.name}</h3>
                    {location.latitude && location.longitude ? (
                      <Badge variant="default" className="mt-1 bg-green-100 text-green-700">
                        <Check className="w-3 h-3 mr-1" /> Geolocalización activa
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="mt-1">
                        <AlertCircle className="w-3 h-3 mr-1" /> Sin coordenadas
                      </Badge>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEditDialog(location)}>
                      <Edit2 className="w-4 h-4 mr-2" /> Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={() => handleDelete(location.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-2 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-2">{location.address}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Navigation className="w-4 h-4 text-primary" />
                  <span>Radio: {location.geofence_radius_m}m</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <span>PIN: ****{location.staff_pin.slice(-2)}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingLocation ? 'Editar local' : 'Nuevo local'}
            </DialogTitle>
            <DialogDescription>
              Configura la ubicación y el PIN de respaldo para validar visitas
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label>Nombre del local</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Cafetería Centro"
                required
                data-testid="location-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Dirección completa</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.address}
                  onChange={(e) => {
                    setFormData({ ...formData, address: e.target.value });
                    setGeocodeStatus(null); // Reset status when address changes
                  }}
                  placeholder="Ej: Calle Mayor 10, Madrid, España"
                  required
                  className={`flex-1 ${geocodeStatus === 'success' ? 'border-green-500' : geocodeStatus === 'error' ? 'border-red-500' : ''}`}
                  data-testid="location-address-input"
                />
                <Button 
                  type="button" 
                  variant={geocodeStatus === 'success' ? 'default' : 'outline'}
                  onClick={handleGeocode}
                  disabled={geocoding}
                  className={geocodeStatus === 'success' ? 'bg-green-600 hover:bg-green-700' : ''}
                  title="Buscar dirección"
                >
                  {geocoding ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : geocodeStatus === 'success' ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Pulsa el botón de búsqueda para intentar localizar automáticamente. Si no es preciso, ajusta las coordenadas manualmente.
              </p>
              
              {/* Geocode Status Feedback */}
              {geocodeStatus === 'success' && formData.latitude && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Coordenadas encontradas - verifica que sean correctas abajo
                </p>
              )}
              {geocodeStatus === 'error' && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> No se encontró la dirección. Introduce las coordenadas manualmente.
                </p>
              )}
            </div>

            {/* Manual Coordinates Entry - Primary Method */}
            <div className="space-y-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">Coordenadas del local</p>
                  <p className="text-xs text-slate-600 mt-1">
                    Haz clic en el mapa para seleccionar la ubicación exacta de tu local
                  </p>
                </div>
              </div>
              
              {/* Interactive Map */}
              <LocationMapPicker
                latitude={formData.latitude}
                longitude={formData.longitude}
                onLocationSelect={(lat, lng) => {
                  setFormData({ ...formData, latitude: lat, longitude: lng });
                  setGeocodeStatus('success');
                  toast.success('Ubicación seleccionada en el mapa');
                }}
              />
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Latitud</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.latitude || ''}
                    onChange={(e) => {
                      setFormData({ ...formData, latitude: e.target.value ? parseFloat(e.target.value) : null });
                      if (e.target.value) setGeocodeStatus('success');
                    }}
                    placeholder="Ej: 41.385063"
                    className="text-sm font-mono"
                    data-testid="location-latitude-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Longitud</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.longitude || ''}
                    onChange={(e) => {
                      setFormData({ ...formData, longitude: e.target.value ? parseFloat(e.target.value) : null });
                      if (e.target.value) setGeocodeStatus('success');
                    }}
                    placeholder="Ej: 2.173404"
                    className="text-sm font-mono"
                    data-testid="location-longitude-input"
                  />
                </div>
              </div>
              
              {/* Verify in Google Maps Link */}
              {formData.latitude && formData.longitude && (
                <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                  <div className="flex items-center gap-2 text-green-700">
                    <Check className="w-4 h-4" />
                    <span className="text-sm font-medium">Coordenadas configuradas</span>
                  </div>
                  <a 
                    href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1 font-medium"
                  >
                    Verificar en Maps
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Radio del geofence</Label>
                <span className="text-sm font-medium text-primary">{formData.geofence_radius_m}m</span>
              </div>
              <Slider
                value={[formData.geofence_radius_m]}
                onValueChange={(value) => setFormData({ ...formData, geofence_radius_m: value[0] })}
                min={25}
                max={500}
                step={25}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-500">
                <span>25m (preciso)</span>
                <span>500m (amplio)</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>PIN de respaldo (4-6 dígitos)</Label>
              <Input
                type="password"
                value={formData.staff_pin}
                onChange={(e) => setFormData({ ...formData, staff_pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                placeholder="Ej: 1234"
                required
                maxLength={6}
                data-testid="location-pin-input"
              />
              <p className="text-xs text-slate-500">
                Este PIN lo usará el personal para validar clientes cuando la geolocalización falle
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1 btn-primary"
                data-testid="location-submit-btn"
              >
                {editingLocation ? 'Guardar cambios' : 'Crear local'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upgrade Plan Modal */}
      <UpgradePlanModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        title="Límite de locales alcanzado"
        message={upgradeMessage}
      />
    </div>
  );
};

export default LocationsPage;
